import { Placement, DeviceFamily, MarketPlace } from '@amzn/fire-tv-campaign-automation-service';
import { useMemo } from 'react';
import { EMBEDDED_URL_PARAMS, EMBEDDED_MODE_VALUE } from 'src/constants';
import { isFeatureEnabled, FeatureFlag } from 'src/featureFlags';
import { logger } from 'src/logger';

interface EmbeddedModeState {
  isEmbedded: boolean;
  embeddedPlacement: Placement | null;
  embeddedDeviceFamily: DeviceFamily | null;
  embeddedMarketplace: MarketPlace | null;
}

const NON_EMBEDDED: EmbeddedModeState = {
  isEmbedded: false,
  embeddedPlacement: null,
  embeddedDeviceFamily: null,
  embeddedMarketplace: null,
};

const validateEnum = <T extends Record<string, string>>(
  value: string | null,
  enumObj: T,
  name: string,
): T[keyof T] | null => {
  if (!value) return null;
  const enumValues = Object.values(enumObj) as string[];
  if (enumValues.includes(value)) return value as T[keyof T];
  const sanitized = value.replace(/[\n\r]/g, '').slice(0, 100);
  logger.warn('useEmbeddedMode: Invalid enum param', { name, value: sanitized });
  return null;
};

// URL params are parsed once — they don't change during SPA lifecycle
/**
 * Detects embedded mode from URL parameters and returns parsed configuration.
 * Returns non-embedded defaults when the EmbeddedMode feature flag is disabled
 * or when ?mode=embedded is absent. Validates placement, deviceFamily, and
 * marketplace against their respective enums.
 */
export const useEmbeddedMode = (): EmbeddedModeState => {
  return useMemo(() => {
    if (!isFeatureEnabled(FeatureFlag.EmbeddedMode)) return NON_EMBEDDED;
    if (typeof window === 'undefined') return NON_EMBEDDED;
    const params = new URLSearchParams(window.location.search);
    if (params.get(EMBEDDED_URL_PARAMS.MODE) !== EMBEDDED_MODE_VALUE) return NON_EMBEDDED;
    return {
      isEmbedded: true,
      embeddedPlacement: validateEnum(
        params.get(EMBEDDED_URL_PARAMS.PLACEMENT),
        Placement,
        'placement',
      ),
      embeddedDeviceFamily: validateEnum(
        params.get(EMBEDDED_URL_PARAMS.DEVICE_FAMILY),
        DeviceFamily,
        'deviceFamily',
      ),
      embeddedMarketplace: validateEnum(
        params.get(EMBEDDED_URL_PARAMS.MARKETPLACE),
        MarketPlace,
        'marketplace',
      ),
    };
  }, []);
};
